import { apiGetJSON, apiGetText } from "../client.js";
import {
  cancelRequest,
  composeAbortSignal,
  isAbortError,
  requestsCurrent,
  startRequest
} from "../core/request-coordinator.js";
import {
  normalizeTurnDiagnostics,
  normalizeWorkspaceDiagnostics
} from "../domain/diagnostics.js";
import {
  normalizeTurnReadModel,
  normalizeTurnRefreshReadModel
} from "../domain/readmodels.js";
import {
  applyTurnDetailState,
  applyTurnRefreshSnapshot,
  buildNotFoundTurnDetailState,
  clearTurnDetailLiveState,
  resetTurnDetailState,
  selectedTurnArtifact
} from "./state.js";

const snapshotDeps = {
  async fetchTurnDetailSnapshot(apiKey, turnID, selectedArtifactID, options = {}) {
    const snapshot = await apiGetJSON(apiKey, `/internal/readmodels/turns/${encodeURIComponent(turnID)}`, {
      signal: options.signal
    });
    return withTurnDiagnostics(apiKey, normalizeTurnReadModel(snapshot, selectedArtifactID, { activeTab: "output" }), turnID, options.signal);
  },
  async fetchTurnRefreshSnapshot(apiKey, turnID, options = {}) {
    const suffix = options.includeEvents === false ? "?include_events=false" : "";
    const snapshot = await apiGetJSON(apiKey, `/internal/readmodels/turns/${encodeURIComponent(turnID)}${suffix}`, {
      signal: options.signal
    });
    return withTurnDiagnostics(apiKey, normalizeTurnRefreshReadModel(snapshot), turnID, options.signal);
  },
  apiGetText
};

export async function loadTurnDetail(state, apiKey, turnID, options = {}) {
  if (!apiKey) {
    (options.resetTurnDetailState || resetTurnDetailState)(state);
    return false;
  }

  const deps = { ...snapshotDeps, ...(options.deps || {}) };
  const snapshotRequest = startRequest(state.requests.snapshot);
  const signal = composeAbortSignal(snapshotRequest.signal, options.signal, options.request?.signal);
  try {
    const snapshot = await deps.fetchTurnDetailSnapshot(apiKey, turnID, state.selectedArtifactID, { signal });
    if (!requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    if (options.cancelArtifactRequest) {
      options.cancelArtifactRequest();
    } else {
      cancelRequest(state.requests.artifactBody);
    }
    applyTurnDetailState(state, snapshot);
    options.onTurnMerged?.(snapshot.turn);
    return true;
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    if (isMissingTurnError(error)) {
      clearTurnDetailLiveState(state);
      if (options.cancelArtifactRequest) {
        options.cancelArtifactRequest();
      } else {
        cancelRequest(state.requests.artifactBody);
      }
      applyTurnDetailState(state, buildNotFoundTurnDetailState(state.activeTab));
      return true;
    }
    throw error;
  }
}

export async function refreshTurnDetail(state, apiKey, turnID, options = {}) {
  if (!apiKey) {
    return false;
  }

  const deps = { ...snapshotDeps, ...(options.deps || {}) };
  const snapshotRequest = startRequest(state.requests.snapshot);
  const signal = composeAbortSignal(snapshotRequest.signal, options.signal, options.request?.signal);
  try {
    const snapshot = await deps.fetchTurnRefreshSnapshot(apiKey, turnID, {
      includeEvents: options.includeEvents !== false,
      signal
    });
    if (!requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    applyTurnRefreshSnapshot(state, snapshot);
    options.onTurnMerged?.(snapshot.turn);
    return true;
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    if (isMissingTurnError(error)) {
      clearTurnDetailLiveState(state);
      if (options.cancelArtifactRequest) {
        options.cancelArtifactRequest();
      } else {
        cancelRequest(state.requests.artifactBody);
      }
      applyTurnDetailState(state, buildNotFoundTurnDetailState(state.activeTab));
      return true;
    }
    throw error;
  }
}

export async function loadSelectedArtifactContent(state, apiKey, options = {}) {
  const artifact = selectedTurnArtifact(state);
  if (!artifact || !artifact.storage_path) {
    return;
  }

  const deps = { ...snapshotDeps, ...(options.deps || {}) };
  const request = startRequest(state.requests.artifactBody);
  const selectedArtifactID = artifact.id;
  const selectedTurnID = artifact.turn_id;
  options.setStatus?.("Loading artifact...", "busy");
  options.render?.();
  try {
    const body = await deps.apiGetText(
      apiKey,
      `/v1/turns/${encodeURIComponent(artifact.turn_id)}/artifacts/${encodeURIComponent(artifact.id)}/content`,
      { signal: request.signal }
    );
    if (!request.isCurrent()) {
      return;
    }
    const currentArtifact = selectedTurnArtifact(state);
    if (!currentArtifact || currentArtifact.id !== selectedArtifactID || currentArtifact.turn_id !== selectedTurnID) {
      return;
    }
    state.artifactBody = body;
    state.artifactError = "";
    options.setStatus?.("Artifact loaded.", "ok");
  } catch (error) {
    if (isAbortError(error) || !request.isCurrent()) {
      return;
    }
    const currentArtifact = selectedTurnArtifact(state);
    if (!currentArtifact || currentArtifact.id !== selectedArtifactID || currentArtifact.turn_id !== selectedTurnID) {
      return;
    }
    state.artifactBody = "";
    state.artifactError = error.message;
    options.setStatus?.(error.message, "error");
  }
  options.render?.();
}

function isMissingTurnError(error) {
  const message = String(error?.message || "");
  return message.includes("run not found") || message.includes("turn not found");
}

async function withTurnDiagnostics(apiKey, snapshot, turnID, signal) {
  const [turnDiagnostics, workspaceDiagnostics] = await Promise.all([
    apiGetJSON(apiKey, `/v1/turns/${encodeURIComponent(turnID)}/diagnostics`, { signal }),
    snapshot?.turn?.workspace_id
      ? apiGetJSON(apiKey, `/v1/workspaces/${encodeURIComponent(snapshot.turn.workspace_id)}/diagnostics`, { signal })
      : Promise.resolve(null)
  ]);
  return {
    ...snapshot,
    turnDiagnostics: normalizeTurnDiagnostics(turnDiagnostics),
    workspaceDiagnostics: workspaceDiagnostics ? normalizeWorkspaceDiagnostics(workspaceDiagnostics) : null
  };
}
